import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications } from '@/contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { Bell, X } from 'lucide-react';
import { useState } from 'react';

interface NotificationBellProps {
  onTaskClick?: (taskId: string) => void;
  userRole?: string | undefined;
}

export default function NotificationBell({
  onTaskClick,
  userRole,
}: NotificationBellProps = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
  } = useNotifications();

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'task_created':
        return '🆕';
      case 'task_updated':
        return '📝';
      case 'task_assigned':
        return '👤';
      case 'task_approved':
        return '✅';
      case 'task_completed_by_assignee':
        return '✅';
      case 'task_pending_approval':
        return '⏳';
      case 'help_requested':
        return '🆘';
      case 'comment_added':
        return '💬';
      case 'extension_request':
        return '📅';
      default:
        return '📢';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'task_created':
        return 'text-blue-600';
      case 'task_updated':
        return 'text-yellow-600';
      case 'task_assigned':
        return 'text-purple-600';
      case 'task_approved':
        return 'text-green-600';
      case 'task_completed_by_assignee':
        return 'text-green-600';
      case 'task_pending_approval':
        return 'text-orange-600';
      case 'help_requested':
        return 'text-red-600';
      case 'comment_added':
        return 'text-indigo-600';
      case 'extension_request':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleNotificationClick = (notification: any) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    // Navigate to task view for every role (admin, GM, PM, HOD, staff, etc.)
    if (notification.taskId && onTaskClick) {
      onTaskClick(notification.taskId);
    }
    setIsOpen(false);
  };

  // Extract task name from message (task names are typically in quotes)
  const extractTaskName = (message: string): { taskName: string | null; cleanedMessage: string } => {
    // Try to find text in quotes (e.g., "task name" has been updated)
    const quotedMatch = message.match(/"([^"]+)"/);
    if (quotedMatch && quotedMatch[1]) {
      const taskName = quotedMatch[1];
      // Remove the quoted task name from the message
      const cleanedMessage = message.replace(`"${taskName}"`, '').trim();
      return { taskName, cleanedMessage };
    }
    return { taskName: null, cleanedMessage: message };
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs animate-pulse"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs"
              >
                Mark all read
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllNotifications}
              className="text-xs text-red-600 hover:text-red-700"
            >
              Clear all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="p-1 h-6 w-6"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>

        <ScrollArea className="h-64">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              No notifications yet
            </div>
          ) : (
            <div className="p-1">
              {notifications.map(notification => {
                const { taskName, cleanedMessage } = extractTaskName(notification.message);
                return (
                  <DropdownMenuItem
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-3 cursor-pointer ${
                      !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between w-full gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm flex-shrink-0">
                            {getNotificationIcon(notification.type)}
                          </span>
                          <div
                            className={`font-medium text-sm ${getNotificationColor(notification.type)}`}
                          >
                            {notification.title}
                          </div>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1 flex-shrink-0"></div>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {cleanedMessage}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDistanceToNow(notification.timestamp, {
                            addSuffix: true,
                          })}
                        </div>
                      </div>
                      {taskName && (
                        <div className="flex-shrink-0">
                          <div className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 rounded-md text-xs font-semibold max-w-[150px] truncate" title={taskName}>
                            {taskName}
                          </div>
                        </div>
                      )}
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t bg-gray-50 dark:bg-gray-800">
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
